import React, { FC, useEffect, useState } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { isEmpty } from 'lodash'
import { FlowName } from './config'
import { StateRoute } from './types/interface/fsm.interface'
import { FSMService, FSMSwitch, FSMMatch } from './FSM'
import FSMProtectedRoute from './routes/FSMProtectedRoute'
import { QBCustomAuth } from './pages/qb'
import PaymentHandlingPage from './pages/shared/PaymentHandlingPage'
import AppWrapper from './AppWrapper'
import Sentry from './service/sentry.service'
import AgentLoginPage from './pages/qb/AgentLogin/AgentLoginPage'
import {
  PAYMENT_CALLBACK_PATH_REGEXP,
  PAYMENT_CALLBACK_STATUSES,
} from './constants/route-constants'
import { signOutOnDemand } from '@dg-util/productHandler'
import SwitchFlowDEVButton from '@dg-shared/SwitchFlowDEVButton'
import { FSMCommonTransitions } from './FSM/shared/constants'
import { ProductName } from './types/interface/quote.interface'

export const RoutedContent: FC<{ routes: StateRoute[] }> = ({ routes }) => {
  const location = useLocation()
  // Deliberately a regexp rather than `matchPath`: v6 flipped that function's argument order to
  // (pattern, pathname) and both arguments are strings, so a wrong order compiles and silently
  // never matches — which would break payment callbacks with no test failure.
  const isPaymentCallbackRoute = PAYMENT_CALLBACK_PATH_REGEXP.test(location.pathname)

  return (
    <>
      {!isPaymentCallbackRoute && (
        <FSMSwitch>
          {!isEmpty(routes) &&
            routes.map((route: StateRoute, idx: number) => (
              <FSMMatch key={`${idx}-route`} is={route.is} component={route.page} />
            ))}
        </FSMSwitch>
      )}
      <Routes>
        {/* One route per status: v6+ dropped v5's regex param syntax, and a bare
            `/payment/:paymentStatus` would also match unknown statuses, which v5 let fall through. */}
        {PAYMENT_CALLBACK_STATUSES.map((paymentStatus) => (
          <Route
            key={paymentStatus}
            path={`/payment/${paymentStatus}`}
            element={<FSMProtectedRoute component={PaymentHandlingPage} />}
          />
        ))}
        <Route path='/custom-auth' element={<FSMProtectedRoute component={QBCustomAuth} />} />
        {/* Agent login handling */}
        <Route path='/agent' element={<AgentLoginPage />} />

        {/* <Routes> throws on any non-<Route> child, so the DEV-only switch button becomes the
            no-match route — the same slot v5's path-less last <Switch> child gave it. */}
        <Route
          path='*'
          element={
            <SwitchFlowDEVButton
              targetStateValue={FSMCommonTransitions.QB_FIND}
              targetFlow={FlowName.QUOTE_AND_BUY}
              targetProduct={ProductName.VEHICLE}
              FSMService={FSMService}
              label='Switch to other flow state'
            />
          }
        />
      </Routes>
    </>
  )
}

const App: FC = () => {
  const [routes, setRoutes] = useState<StateRoute[]>(FSMService.routes)

  useEffect(() => {
    // Do a signOut action if it was requested by the productHandler on app initialization
    // This should clean the previous redis auth key in case the flow/product was changed in the URL directly
    ;(async () => signOutOnDemand())()

    // Register routes update on FSM re-initialization
    FSMService.setInitMachineCallback((fsmService) => {
      setRoutes(fsmService.routes)
    })
  }, [])

  const content = <RoutedContent routes={routes} />

  return <AppWrapper>{content}</AppWrapper>
}

export default Sentry.withProfiler(App)
